import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  FormStack,
  Input,
  LoadingState,
  Modal,
  Select,
  Text,
} from "@marekl/adminer/components";
import { asApiErrorBody } from "@marekl/adminer/api";
import { ApiError } from "../../../lib/api";
import { useGlobalPersons, type PersonListItem } from "../../../lib/queries/persons";
import { useSetTeamLeader } from "../../../lib/queries/teams";
import type { ID } from "../../../lib/id";
import type { Membership } from "../../../lib/queries/memberships";

/**
 * Who may lead, said for the roster the team can actually hold: the leader
 * holds the best kind their team's roster can hold (team.SetLeader), so a
 * registered člen only where the team has registered into the season.
 */
const LEADER_RULE: Record<Membership["kind"], string> = {
  registered: "Vedoucím může být jen registrovaný člen týmu",
  guest: "Vedoucím může být kterýkoli člen nebo nečlen ze soupisky",
  foreign: "Vedoucím může být kterýkoli zahraniční člen ze soupisky",
};

/**
 * ChangeLeaderModal hands a team to another of its registered members.
 *
 * Candidates come from the registry filtered by team rather than from the
 * roster endpoint, because the decision needs one fact the roster does not
 * carry: whether the person can already log in. Leadership is what grants the
 * team_leader role, so handing the team to someone with no account leaves the
 * team unadministrable — the dialog offers to provision one in the same call.
 *
 * The old leader is not touched beyond losing the leadership: they keep their
 * membership, their roles and their account.
 */
export function ChangeLeaderModal({
  teamId,
  currentLeaderPersonId,
  leaderKind = "registered",
  open,
  onClose,
}: {
  teamId: ID;
  currentLeaderPersonId?: ID;
  /** The kind the team's roster holds at best, which a leader must hold. */
  leaderKind?: Membership["kind"];
  open: boolean;
  onClose: () => void;
}) {
  const { data, isLoading } = useGlobalPersons({ team_id: teamId, per_page: 200 });
  const setLeader = useSetTeamLeader(teamId);

  const candidates = useMemo(
    () => (data?.items ?? []).filter((p) => p.id !== currentLeaderPersonId),
    [data?.items, currentLeaderPersonId],
  );

  const [personId, setPersonId] = useState<ID | null>(null);
  const [createLogin, setCreateLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{
    name: string;
    // Set ONLY when the server has no mail configured; then the admin has to
    // relay the link. With mail working the new leader gets it directly and
    // nothing secret appears on this screen.
    setPasswordURL?: string;
    loginCreated?: boolean;
    assistantDropped?: boolean;
  } | null>(null);

  const selected: PersonListItem | undefined = candidates.find((p) => p.id === personId);
  const hasLogin = !!selected?.login_email;

  // Someone with no account needs one to lead, so the offer is on by default
  // for them and impossible for everyone else. Keyed on the person's id —
  // depending on the object would re-fire on every background refetch (new
  // array, new objects) and wipe whatever e-mail the admin was typing.
  const selectedId = selected?.id ?? null;
  const selectedHasLogin = !!selected?.login_email;
  useEffect(() => {
    if (selectedId == null) return;
    setCreateLogin(!selectedHasLogin);
    setEmail("");
  }, [selectedId, selectedHasLogin]);

  useEffect(() => {
    if (!open) {
      setPersonId(null);
      setCreateLogin(false);
      setEmail("");
      setError(null);
      setDone(null);
    }
  }, [open]);

  async function onSubmit() {
    if (!selected) return;
    setError(null);
    const name = `${selected.first_name} ${selected.last_name}`;
    try {
      const res = await setLeader.mutateAsync({
        person_id: selected.id,
        login: createLogin ? { email: email.trim(), display_name: name } : undefined,
      });
      setDone({
        name,
        setPasswordURL: res.set_password_url,
        loginCreated: res.login_created,
        assistantDropped: res.assistant_role_dropped,
      });
    } catch (err) {
      // asApiErrorBody is the lib's single source of truth for the error
      // shape — a bare cast missed the legacy `error` field it recognises.
      setError(
        (err instanceof ApiError ? asApiErrorBody(err.body)?.message : undefined) ??
          "Vedoucího se nepodařilo změnit.",
      );
    }
  }

  return (
    <Modal
      open={open}
      title={currentLeaderPersonId ? "Změnit vedoucího" : "Přiřadit vedoucího"}
      onClose={onClose}
      // Deliberately a Modal and not a FormModal, unlike every other form
      // dialog in the app: the footer has two states. Once the handover has
      // gone through, `done` collapses it to a single „Zavřít" over a summary
      // of what was written, and FormModal always renders a cancel plus a
      // submit. Converting it would mean offering „Předat vedení" again on a
      // team that already has its new leader.
      footer={
        done ? (
          <Button variant="primary" onClick={onClose}>
            Zavřít
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>
              Zrušit
            </Button>
            <Button
              variant="primary"
              loading={setLeader.isPending}
              disabled={!selected || (createLogin && !email.trim())}
              onClick={onSubmit}
            >
              {currentLeaderPersonId ? "Předat vedení" : "Přiřadit"}
            </Button>
          </>
        )
      }
    >
      {done ? (
        <FormStack>
          <Alert variant="ok" title="Vedení předáno">
            Tým nyní vede {done.name}.
            {done.assistantDropped && " Roli asistenta jsme odebrali; vedoucí ji mít nemůže."}
          </Alert>
          {done.loginCreated && !done.setPasswordURL && (
            <Alert variant="info" title="Účet je založený">
              Novému vedoucímu jsme poslali odkaz pro nastavení hesla. Platí sedm dní.
            </Alert>
          )}
          {done.setPasswordURL && (
            // Only when the server cannot send mail. Then somebody has to
            // carry the link, and it is better said out loud than hidden.
            <Alert variant="warn" title="Odesílání e-mailů není nastavené">
              Předejte novému vedoucímu tenhle odkaz; nastaví si jím heslo. Zobrazuje se jen
              teď.
              <br />
              <code style={{ wordBreak: "break-all" }}>{done.setPasswordURL}</code>
            </Alert>
          )}
        </FormStack>
      ) : isLoading ? (
        <LoadingState label="Načítám soupisku…" />
      ) : candidates.length === 0 ? (
        <Alert variant="warn">
          Na soupisce aktivní sezóny není nikdo další. {LEADER_RULE[leaderKind]}; přidejte ho
          nejdřív na soupisku.
        </Alert>
      ) : (
        <FormStack>
          {error && <Alert variant="err">{error}</Alert>}
          <Select
            label="Nový vedoucí"
            required
            value={personId != null ? String(personId) : ""}
            onChange={(e) => setPersonId(e.target.value ? e.target.value : null)}
          >
            <option value="">— vyberte člena —</option>
            {candidates.map((p) => (
              <option key={p.id} value={p.id}>
                {p.last_name} {p.first_name}
                {p.login_email ? ` · ${p.login_email}` : " · bez účtu"}
              </option>
            ))}
          </Select>
          {currentLeaderPersonId && (
            <Text variant="muted">
              Dosavadní vedoucí zůstává na soupisce i se svými rolemi, jen přestane tým spravovat.
            </Text>
          )}
          {selected && !hasLogin && (
            <>
              <Checkbox
                label="Vytvořit účet"
                checked={createLogin}
                onChange={(e) => setCreateLogin(e.target.checked)}
              />
              <Text variant="muted">
                Bez účtu nebude mít nový vedoucí jak tým spravovat.
              </Text>
            </>
          )}
          {selected && hasLogin && (
            <Text variant="muted">Účet už má: {selected.login_email}</Text>
          )}
          {createLogin && !hasLogin && (
            <>
              <Input
                label="Přihlašovací e-mail"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Text variant="muted" as="p">
                Heslo tu nezadáváte. Nový vedoucí dostane e-mailem odkaz, kterým si ho
                nastaví sám.
              </Text>
            </>
          )}
        </FormStack>
      )}
    </Modal>
  );
}
